import React from "react"
import { orderBy, take } from "lodash"
import {
  List,
  ListItem,
  makeStyles,
  ListItemIcon,
  Checkbox,
  ListItemText,
  Typography,
  Box,
  TextField,
  InputAdornment,
  Button,
} from "@material-ui/core"
import { Search } from "@material-ui/icons"
import { Tag } from "./Tag"
import { FixedSizeList } from "react-window"
import AutoSizer from "react-virtualized-auto-sizer"
import { SearchQueryParameter } from "src/useSearchParameters"

const useMultiSelectTagGroupStyles = makeStyles(() => ({
  seeMoreButton: {
    width: "100%",
  },
  hideScrollBar: {
    "-ms-overflow-style": "none" /* IE and Edge */,
    scrollbarWidth: "none" /* Firefox */,
    "&::-webkit-scrollbar": {
      display: "none", // Chrome, safari
    },
  },
}))

interface TagListItemProps {
  index: number
  style: React.CSSProperties
  data: {
    allTags: Tag[]
    selectedTags: Set<string>
    tagCounts: Record<string, number>
    onTagSelected: (type: SearchQueryParameter, value: string) => void
  }
}
const TagListItem: React.FC<TagListItemProps> = ({ data, index, style }) => {
  const { allTags, selectedTags, tagCounts, onTagSelected } = data
  const tag = allTags[index]

  return (
    <ListItem
      key={tag.value}
      style={style}
      role={undefined}
      button
      dense
      disabled={!selectedTags.has(tag.id) && !tagCounts[tag.id]}
      onClick={() => onTagSelected(tag.type, tag.value)}
    >
      <ListItemIcon>
        <Checkbox
          edge="start"
          checked={selectedTags.has(tag.id)}
          tabIndex={-1}
          disableRipple
          inputProps={{ "aria-labelledby": tag.value || "" }}
        />
      </ListItemIcon>
      <ListItemText primary={tag.value} />
    </ListItem>
  )
}

interface MultiSelectTagGroupProps {
  name: string
  tags: Tag[]
  selectedTags: Set<string>
  tagCounts: Record<string, number>
  onTagSelected: (type: SearchQueryParameter, value: string) => void
}

export const MultiSelectTagGroup: React.FC<MultiSelectTagGroupProps> = ({
  name,
  tags,
  selectedTags,
  tagCounts,
  onTagSelected,
}) => {
  const [filter, setFilter] = React.useState<string | undefined>()
  const [seeMore, setSeeMore] = React.useState<boolean>()
  const classes = useMultiSelectTagGroupStyles()

  const closeShowAll = () => {
    setFilter(undefined)
    setSeeMore(false)
  }

  const [sortedByCount, sortedByTitle] = React.useMemo(
    () => [
      orderBy(
        tags,
        tag => (tagCounts ? tagCounts[tag.id] ?? 0 : tag.count ?? 0),
        "desc"
      ),
      orderBy(tags, tag => tag.value, "asc"),
    ],
    [tags, tagCounts]
  )

  const first = take(sortedByCount, 5)
  const rest = sortedByTitle.filter(tag =>
    filter ? tag.value?.toLowerCase().includes(filter.toLowerCase()) : true
  )
  const tagListItemData = {
    allTags: rest,
    selectedTags,
    tagCounts,
    onTagSelected,
  }

  const handleFilterChange = (evt: any) => setFilter(evt.target.value)

  return (
    <>
      <Box ml={1} mt={1}>
        <Typography variant="subtitle2" component="h2">
          {name}
        </Typography>
      </Box>
      <List dense>
        {first.map(tag => (
          <ListItem
            key={tag.value}
            role={undefined}
            button
            dense
            disabled={!selectedTags.has(tag.id) && !tagCounts[tag.id]}
            onClick={() => onTagSelected(tag.type, tag.value)}
          >
            <ListItemIcon>
              <Checkbox
                edge="start"
                checked={selectedTags.has(tag.id)}
                tabIndex={-1}
                disableRipple
                inputProps={{ "aria-labelledby": tag.value || "" }}
              />
            </ListItemIcon>
            <ListItemText primary={tag.value} />
          </ListItem>
        ))}
      </List>
      {seeMore ? (
        <>
          <Box ml={1} mr={1} mb={1}>
            <TextField
              variant="outlined"
              size="small"
              label="Search"
              onChange={handleFilterChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }}
            ></TextField>
          </Box>
          <AutoSizer disableHeight>
            {({ width }) => (
              <FixedSizeList
                className={classes.hideScrollBar}
                height={400}
                width={width}
                itemSize={46}
                itemCount={rest.length}
                itemKey={i => rest[i].id}
                itemData={tagListItemData}
              >
                {TagListItem}
              </FixedSizeList>
            )}
          </AutoSizer>

          <Button className={classes.seeMoreButton} onClick={closeShowAll}>
            Show Less
          </Button>
        </>
      ) : (
        <Button
          className={classes.seeMoreButton}
          onClick={() => setSeeMore(true)}
        >{`Show All ${rest.length}`}</Button>
      )}
    </>
  )
}
